import { useCallback, useEffect, useRef, useState } from 'react';
import { getWorkDayEndMs } from '../utils/workSchedule';
import {
  ACTIVE_STATUSES,
  FINISHED_STATUSES,
  PRODUCTIVITY_ROLES,
  buildSavePayload,
  clearTimerState,
  finalizeTimer,
  getElapsedSeconds,
  loadTimerState,
  pauseTimer,
  resumeTimer,
  saveTimerState,
  settleRunningState,
  shouldAutoStart,
  startTimer,
} from '../utils/serviceProductivityTimer';

const DISABLED_TIMER = {
  elapsedSeconds: 0,
  isRunning: false,
  isPaused: false,
  isFinalized: false,
  isActive: false,
  canControl: false,
  enabled: false,
  pause: () => {},
  resume: () => {},
  autoStartIfNeeded: () => null,
  autoFinalizeIfNeeded: () => null,
  getSavePayload: () => ({}),
  resetAfterSave: () => {},
};

/**
 * Cronómetro de productividad ligado al ciclo del servicio.
 * role: 'mechanic' | 'assistant'
 * enabled: si false, no corre ni persiste (p. ej. sin ayudante asignado)
 */
export function useServiceProductivityTimer(
  vehicleService,
  appointmentStatus,
  { role = 'mechanic', enabled = true } = {},
) {
  const roleConfig = PRODUCTIVITY_ROLES[role] || PRODUCTIVITY_ROLES.mechanic;
  const vehicleServiceId = vehicleService?.id;
  const [timerState, setTimerState] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerStateRef = useRef(null);
  timerStateRef.current = timerState;

  const persist = useCallback(
    (next) => {
      setTimerState(next);
      saveTimerState(next, role);
      setElapsedSeconds(getElapsedSeconds(next));
    },
    [role],
  );

  useEffect(() => {
    if (!enabled || !vehicleServiceId) {
      setTimerState(null);
      setElapsedSeconds(0);
      return;
    }
    const loaded = loadTimerState(
      vehicleServiceId,
      vehicleService,
      appointmentStatus,
      role,
    );
    setTimerState(loaded);
    setElapsedSeconds(getElapsedSeconds(loaded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    vehicleServiceId,
    appointmentStatus,
    role,
    vehicleService?.[roleConfig.totalField],
  ]);

  useEffect(() => {
    if (!enabled || !timerState) return undefined;

    const tick = () => {
      const current = timerStateRef.current;
      if (!current) return;

      if (current.running && !current.finalized && current.segmentStartedAt) {
        const now = Date.now();
        const workEnd = getWorkDayEndMs(current.segmentStartedAt);
        if (now >= workEnd) {
          const paused = pauseTimer(current, workEnd);
          persist(paused);
          return;
        }
      }

      setElapsedSeconds(getElapsedSeconds(current));
    };

    tick();
    if (!timerState.running || timerState.finalized) return undefined;

    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    timerState?.running,
    timerState?.finalized,
    timerState?.productiveMs,
    persist,
  ]);

  const autoStartIfNeeded = useCallback(() => {
    if (!enabled || !vehicleServiceId) return null;
    let payload = null;
    setTimerState((prev) => {
      const base =
        prev ||
        loadTimerState(vehicleServiceId, vehicleService, 'En curso', role);
      if (!shouldAutoStart(base)) return base;
      const next = startTimer(base);
      saveTimerState(next, role);
      setElapsedSeconds(getElapsedSeconds(next));
      payload = buildSavePayload(next, {
        role,
        startValue: vehicleService?.[roleConfig.startField] || new Date(),
      });
      return next;
    });
    return payload;
  }, [enabled, vehicleServiceId, vehicleService, role, roleConfig.startField]);

  const autoFinalizeIfNeeded = useCallback(() => {
    if (!enabled || !vehicleServiceId) return null;
    let payload = null;
    setTimerState((prev) => {
      if (!prev) return prev;
      const settled = settleRunningState(prev);
      const next = finalizeTimer(settled);
      saveTimerState(next, role);
      setElapsedSeconds(getElapsedSeconds(next));
      payload = buildSavePayload(next, {
        role,
        startValue: vehicleService?.[roleConfig.startField],
        endValue: new Date(),
      });
      return next;
    });
    return payload;
  }, [enabled, vehicleServiceId, vehicleService, role, roleConfig.startField]);

  const pause = useCallback(() => {
    if (!enabled) return;
    setTimerState((prev) => {
      if (!prev) return prev;
      const next = pauseTimer(prev);
      saveTimerState(next, role);
      setElapsedSeconds(getElapsedSeconds(next));
      return next;
    });
  }, [enabled, role]);

  const resume = useCallback(() => {
    if (!enabled) return;
    setTimerState((prev) => {
      if (!prev || prev.finalized) return prev;
      const next = resumeTimer(prev);
      saveTimerState(next, role);
      setElapsedSeconds(getElapsedSeconds(next));
      return next;
    });
  }, [enabled, role]);

  const getSavePayload = useCallback(() => {
    if (!enabled) {
      return {
        [roleConfig.totalField]: vehicleService?.[roleConfig.totalField] ?? null,
        [roleConfig.startField]: vehicleService?.[roleConfig.startField] ?? null,
        [roleConfig.endField]: vehicleService?.[roleConfig.endField] ?? null,
      };
    }
    if (!timerState) {
      return {
        [roleConfig.totalField]: vehicleService?.[roleConfig.totalField] ?? null,
        [roleConfig.startField]: vehicleService?.[roleConfig.startField] ?? null,
        [roleConfig.endField]: vehicleService?.[roleConfig.endField] ?? null,
      };
    }
    const settled = settleRunningState(timerState);
    return buildSavePayload(settled, {
      role,
      startValue: vehicleService?.[roleConfig.startField],
      endValue: settled.finalized
        ? vehicleService?.[roleConfig.endField] || new Date()
        : null,
    });
  }, [enabled, timerState, vehicleService, role, roleConfig]);

  const isActive = enabled && ACTIVE_STATUSES.includes(appointmentStatus);
  const isFinished = FINISHED_STATUSES.includes(appointmentStatus);
  const isRunning = isActive && !!timerState?.running && !timerState?.finalized;
  const isPaused =
    isActive && !!timerState && !timerState.running && !timerState.finalized;
  const isFinalized = enabled && isFinished;

  useEffect(() => {
    if (!enabled || !isFinished || !timerState || timerState.finalized) return;
    const next = finalizeTimer(settleRunningState(timerState));
    saveTimerState(next, role);
    setTimerState(next);
    setElapsedSeconds(getElapsedSeconds(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isFinished, timerState?.finalized, appointmentStatus, role]);

  const resetAfterSave = useCallback(() => {
    if (isFinished && vehicleServiceId) {
      clearTimerState(vehicleServiceId, role);
    }
  }, [isFinished, vehicleServiceId, role]);

  if (!enabled) {
    return DISABLED_TIMER;
  }

  return {
    elapsedSeconds,
    isRunning,
    isPaused,
    isFinalized,
    isActive,
    canControl: isActive && !isFinalized,
    enabled: true,
    pause,
    resume,
    autoStartIfNeeded,
    autoFinalizeIfNeeded,
    getSavePayload,
    resetAfterSave,
  };
}
